import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useFiscalYear } from '../../context/FiscalYearContext';
import { motion } from 'framer-motion';
import {
  Search, ChevronUp, ChevronDown, X, Edit3, Trash2, Table, Package, FileText,
  Settings2, Eye, EyeOff, ArrowUp, ArrowDown, RotateCcw,
} from 'lucide-react';

// ─── Column definitions ────────────────────────────────────────────────
const STORAGE_KEY = 'carbonscope_table_columns';

// RESP-3b — colonnes conservées sous lg. À 390px, huit colonnes laissaient
// ~45px chacune ; ces quatre en obtiennent ~90. Le choix suit l'usage mobile
// visé : comparer des émissions, et pouvoir ouvrir le détail.
//   name       le libellé de l'activité
//   quantity   porte AUSSI l'unité (quantité et unité y sont concaténées),
//              sans quoi le chiffre d'émission perdrait son référentiel
//   emissions  la valeur à comparer
//   actions    l'accès au détail, déjà offert par son bouton d'édition —
//              c'est ce qui dispense d'ajouter une interaction sur la ligne
// La liste est connue à l'écriture : les classes qui en découlent sont donc
// statiques, jamais issues d'une mesure de viewport au runtime.
//
// On SOUSTRAIT seulement : ces clés sont conservées si elles sont déjà
// affichées, jamais réintroduites. Une colonne masquée par l'utilisateur sur
// desktop le reste sous lg — sa préférence n'est ni lue ni réécrite ici.
const PRIORITY_KEYS = ['name', 'quantity', 'emissions', 'actions'];
const isPriority = (key) => PRIORITY_KEYS.includes(key);

const buildDefaultColumns = (t) => [
  { key: 'name',      labelKey: 'tableView.colName',      align: 'left',  sortable: true,  visible: true,  locked: true },
  { key: 'scope',     labelKey: 'tableView.colScope',     align: 'left',  sortable: false, visible: true,  locked: false, scopeOnly: true },
  { key: 'category',  labelKey: 'tableView.colCategory',  align: 'left',  sortable: true,  visible: true,  locked: false },
  { key: 'factor',    labelKey: 'tableView.colFactor',    align: 'left',  sortable: true,  visible: true,  locked: false },
  { key: 'quantity',  labelKey: 'tableView.colQuantity',  align: 'right', sortable: true,  visible: true,  locked: false },
  { key: 'emissions', labelKey: 'tableView.colEmissions', align: 'right', sortable: true,  visible: true,  locked: false },
  { key: 'percent',   labelKey: 'tableView.colPercent',   align: 'right', sortable: true,  visible: true,  locked: false },
  { key: 'source',    labelKey: 'tableView.colSource',    align: 'left',  sortable: true,  visible: false, locked: false },
  { key: 'comment',   labelKey: 'tableView.colComment',   align: 'left',  sortable: false, visible: false, locked: false },
  // FEAT-01d : colonne badge événement extraordinaire (opt-in défaut masquée, triable).
  { key: 'extraordinary', labelKey: 'tableView.colExtraordinary', align: 'center', sortable: true, visible: false, locked: false },
  { key: 'actions',   labelKey: 'tableView.colActions',   align: 'right', sortable: false, visible: true,  locked: true },
];

function loadColumns(t) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultColumns(t);
    const saved = JSON.parse(raw);
    const defaults = buildDefaultColumns(t);
    // Merge saved prefs onto defaults (preserves new columns added later)
    return defaults.map(def => {
      const s = saved.find(c => c.key === def.key);
      return s ? { ...def, visible: def.locked ? true : s.visible, order: s.order } : def;
    }).sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  } catch {
    return buildDefaultColumns(t);
  }
}

function saveColumns(cols) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cols.map((c, i) => ({ key: c.key, visible: c.visible, order: i }))));
}

// ─── Column config popover ─────────────────────────────────────────────
const ColumnConfigPopover = ({ columns, setColumns, isDark, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (key) => {
    setColumns(prev => {
      const next = prev.map(c => c.key === key && !c.locked ? { ...c, visible: !c.visible } : c);
      saveColumns(next);
      return next;
    });
  };

  const move = (key, dir) => {
    setColumns(prev => {
      const idx = prev.findIndex(c => c.key === key);
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      // Don't move past locked columns at edges
      if (prev[target].locked && (target === 0 || target === prev.length - 1)) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      saveColumns(next);
      return next;
    });
  };

  const reset = () => {
    const def = buildDefaultColumns(t);
    setColumns(def);
    saveColumns(def);
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="column-config-btn"
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
        title={t('tableView.configColumns')}
      >
        <Settings2 className="w-5 h-5" />
      </button>

      {open && (
        <div className={`absolute right-0 top-full mt-2 w-72 rounded-xl shadow-2xl border z-50 overflow-hidden ${
          isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('tableView.configColumns')}</span>
            <button onClick={reset} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
              isDark ? 'text-slate-400 hover:bg-slate-700 hover:text-slate-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`} data-testid="column-reset-btn">
              <RotateCcw className="w-3 h-3" />
              {t('tableView.reset')}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {columns.map((col, idx) => (
              <div key={col.key} className={`flex items-center gap-2 px-3 py-2 ${
                isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
              }`}>
                {/* Visibility toggle */}
                <button
                  onClick={() => toggle(col.key)}
                  disabled={col.locked}
                  className={`p-1 rounded transition-colors ${col.locked ? 'opacity-30 cursor-not-allowed' : ''} ${
                    col.visible
                      ? isDark ? 'text-blue-400' : 'text-blue-500'
                      : isDark ? 'text-slate-600' : 'text-gray-300'
                  }`}
                  data-testid={`col-toggle-${col.key}`}
                >
                  {col.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>

                {/* Label */}
                <span className={`flex-1 text-sm ${
                  col.visible
                    ? isDark ? 'text-slate-200' : 'text-gray-700'
                    : isDark ? 'text-slate-500 line-through' : 'text-gray-400 line-through'
                }`}>
                  {t(col.labelKey)}
                </span>

                {/* Move buttons */}
                {!col.locked && (
                  <div className="flex gap-0.5">
                    <button
                      onClick={() => move(col.key, -1)}
                      disabled={idx === 0 || columns[idx - 1]?.locked}
                      className={`p-0.5 rounded transition-colors disabled:opacity-20 ${
                        isDark ? 'text-slate-400 hover:text-white hover:bg-slate-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => move(col.key, 1)}
                      disabled={idx === columns.length - 1 || columns[idx + 1]?.locked}
                      className={`p-0.5 rounded transition-colors disabled:opacity-20 ${
                        isDark ? 'text-slate-400 hover:text-white hover:bg-slate-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Main component ────────────────────────────────────────────────────
const TableViewPanel = ({
  isDark, tableViewScope, scopeLabels, scopes, getScopeActivities,
  getCategoryName, getSubcategoryName, summary, formatEmissions, formatEmissionsForTable,
  handleEditActivityInModal, handleDeleteActivity, PRODUCT_SALE_CATEGORIES,
  onClose,
}) => {
  const { t, language } = useLanguage();
  const { currentFiscalYear } = useFiscalYear();
  // LOT 4 ter : exercice clôturé → boutons Modifier/Supprimer désactivés.
  const isReadonly = currentFiscalYear?.status === 'closed';
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [columns, setColumns] = useState(() => loadColumns(t));
  // FEAT-01d : filtre 3-états événement extraordinaire ('all' | 'hide' | 'only').
  // État local session (pas localStorage) — vue de pilotage saisie.
  const [extraordinaryFilter, setExtraordinaryFilter] = useState('all');
  // FEAT (item 2949441937) : filtre par catégorie. '' = toutes. État local session
  // (calque extraordinaryFilter). Options dérivées des lignes → scope-aware (cf T2).
  const [categoryFilter, setCategoryFilter] = useState('');

  const visibleCols = columns.filter(c => {
    if (!c.visible) return false;
    if (c.scopeOnly && tableViewScope !== null) return false;
    return true;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const rawActivities = getScopeActivities(tableViewScope);
  const totalEmissions = rawActivities.reduce((sum, a) => sum + (a.emissions || 0), 0);
  // FEAT-01g #3 : affichage conditionnel du filtre 3-états — visible uniquement si
  // le périmètre table view contient au moins 1 ligne extraordinaire (Approche 1,
  // déduction pure-locale, gain de place sinon). 🚨 RF1 inchangé (pur affichage).
  const hasExtraordinary = rawActivities.some(a => a.is_extraordinary === true);

  // FEAT (2949441937) : catégories présentes dans la vue courante. Dérivé de
  // rawActivities (PRÉ-SCOPÉ, PAS du filtré) → le select propose toujours toutes
  // les catégories du scope courant (scope-aware) et ne se vide pas en filtrant.
  // Trié par libellé. Affichage conditionnel : masqué si < 2 catégories distinctes.
  const presentCategoryIds = [...new Set(rawActivities.map(a => a.category_id))]
    .filter(Boolean)
    .sort((a, b) => (getCategoryName(a) || '').localeCompare(getCategoryName(b) || ''));

  // FEAT-01d : filtre 3-états extraordinaire AVANT recherche texte.
  const extraFiltered = extraordinaryFilter === 'hide'
    ? rawActivities.filter(a => a.is_extraordinary !== true)
    : extraordinaryFilter === 'only'
      ? rawActivities.filter(a => a.is_extraordinary === true)
      : rawActivities;

  const filtered = searchQuery
    ? extraFiltered.filter(a => {
        const q = searchQuery.toLowerCase();
        return (a.name || '').toLowerCase().includes(q) || (a.emission_factor_name || '').toLowerCase().includes(q)
          || (getCategoryName(a.category_id) || '').toLowerCase().includes(q)
          || (getSubcategoryName?.(a.subcategory_id) || '').toLowerCase().includes(q)
          || (a.factor_snapshot?.name_simple_fr || '').toLowerCase().includes(q)
          || (a.factor_snapshot?.name_fr || '').toLowerCase().includes(q)
          || (a.comments || '').toLowerCase().includes(q);
      })
    : extraFiltered;

  // FEAT (2949441937) : étape filtre catégorie (AND avec extraordinaire + recherche).
  // '' = toutes (court-circuit, aucun filtrage).
  const categoryFiltered = categoryFilter
    ? filtered.filter(a => a.category_id === categoryFilter)
    : filtered;

  const activities = [...categoryFiltered].sort((a, b) => {
    if (!sortField) return 0;
    let va, vb;
    switch (sortField) {
      case 'name': va = (getSubcategoryName?.(a.subcategory_id) || a.name || '').toLowerCase(); vb = (getSubcategoryName?.(b.subcategory_id) || b.name || '').toLowerCase(); break;
      case 'category': va = getCategoryName(a.category_id); vb = getCategoryName(b.category_id); break;
      case 'factor': va = (a.factor_snapshot?.name_simple_fr || a.factor_snapshot?.name_fr || '').toLowerCase(); vb = (b.factor_snapshot?.name_simple_fr || b.factor_snapshot?.name_fr || '').toLowerCase(); break;
      case 'quantity': va = a.quantity || 0; vb = b.quantity || 0; break;
      case 'emissions': va = a.emissions || 0; vb = b.emissions || 0; break;
      case 'percent': va = a.emissions || 0; vb = b.emissions || 0; break;
      case 'source': va = a.sale_id ? 1 : 0; vb = b.sale_id ? 1 : 0; break;
      case 'extraordinary': va = a.is_extraordinary ? 1 : 0; vb = b.is_extraordinary ? 1 : 0; break;
      default: return 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const scopeColorMap = (scope) => {
    if (scope === 'scope1') return { text: 'text-blue-500', bg: 'bg-blue-500' };
    if (scope === 'scope2') return { text: 'text-cyan-500', bg: 'bg-cyan-500' };
    if (scope === 'scope3_3') return { text: 'text-amber-500', bg: 'bg-amber-500' };
    if (scope?.includes('amont') || scope === 'scope3') return { text: 'text-purple-500', bg: 'bg-purple-500' };
    return { text: 'text-indigo-500', bg: 'bg-indigo-500' };
  };

  const headerBg = tableViewScope === null ? 'from-blue-500 to-purple-500'
    : tableViewScope === 'scope1' ? 'from-blue-500 to-blue-600'
    : tableViewScope === 'scope2' ? 'from-cyan-500 to-cyan-600'
    : tableViewScope?.includes('amont') ? 'from-purple-500 to-purple-600'
    : 'from-indigo-500 to-indigo-600';

  const thBase = `py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider select-none ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`;

  // ─── Cell renderers per column key ───────────────────────────────────
  const renderCell = (col, activity) => {
    const activityScope = activity.scope || 'scope1';
    const { text: scopeColor, bg: scopeBgColor } = scopeColorMap(activityScope);
    const isGrouped = activity.group_size > 1;
    const isLinked = activity.group_index > 0;
    const pct = totalEmissions > 0 ? ((activity.emissions || 0) / totalEmissions * 100) : 0;
    const factorName = activity.factor_snapshot?.name_simple_fr || activity.factor_snapshot?.name_fr || activity.emission_factor_name || '—';
    const factorValue = activity.factor_snapshot?.impacts?.[0]
      ? `${activity.factor_snapshot.impacts[0].value} ${activity.factor_snapshot.impacts[0].unit}`
      : (activity.impact_value ? `${activity.impact_value} ${activity.impact_unit || ''}` : '');

    switch (col.key) {
      case 'name': {
        const subcatName = getSubcategoryName?.(activity.subcategory_id);
        return (
          <td key={col.key} className="py-3 px-3">
            <div className="flex items-center gap-1.5">
              {isLinked ? <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>&#8627;</span>
                : isGrouped ? <span className={`text-[10px] px-1 py-0.5 rounded ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>{activity.group_size}x</span>
                : null}
              <span className={`text-sm font-medium truncate max-w-[200px] ${isDark ? 'text-slate-200' : 'text-gray-900'} ${isLinked ? 'opacity-60' : ''}`}>
                {subcatName || activity.name || activity.emission_factor_name || '—'}
              </span>
            </div>
          </td>
        );
      }
      case 'scope':
        return (
          <td key={col.key} className="py-3 px-3">
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium text-white ${scopeBgColor}`}>
              {activityScope === 'scope3_3' ? '3.3' : (scopeLabels[activityScope]?.name || activityScope).replace('Scope ', '')}
            </span>
          </td>
        );
      case 'category':
        return (
          <td key={col.key} className={`py-3 px-3 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'} ${isLinked ? 'opacity-60' : ''}`}>
            <span className="truncate max-w-[150px] block">{getCategoryName(activity.category_id)}</span>
          </td>
        );
      case 'factor':
        return (
          <td key={col.key} className="py-3 px-3">
            <div className={isLinked ? 'opacity-60' : ''}>
              <p className={`text-xs truncate max-w-[180px] ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{factorName}</p>
              {factorValue && <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{factorValue}</p>}
            </div>
          </td>
        );
      case 'quantity':
        return (
          <td key={col.key} className={`py-3 px-3 text-right text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-900'} ${isLinked ? 'opacity-60' : ''}`}>
            {(activity.original_quantity ?? activity.quantity)?.toLocaleString('fr-FR')} {activity.original_unit || activity.unit}
          </td>
        );
      case 'emissions':
        return (
          <td key={col.key} className={`py-3 px-3 text-right font-bold text-sm ${scopeColor} ${isLinked ? 'opacity-70' : ''}`}>
            {formatEmissionsForTable(activity.emissions, summary?.total_emissions)}
          </td>
        );
      case 'percent':
        return (
          <td key={col.key} className="py-3 px-3 text-right">
            <div className="flex items-center justify-end gap-1.5">
              <div className={`w-10 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                <div className={`h-full rounded-full ${scopeBgColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <span className={`text-xs font-medium min-w-[36px] text-right ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {pct < 0.1 && pct > 0 ? '<0.1' : pct.toFixed(1)}%
              </span>
            </div>
          </td>
        );
      case 'source':
        return (
          <td key={col.key} className="py-3 px-3">
            {!isLinked && PRODUCT_SALE_CATEGORIES.includes(activity.category_id) ? (
              activity.sale_id
                ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'}`}><Package className="w-3 h-3 inline mr-0.5" />{t('tableView.srcProduct')}</span>
                : <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}><FileText className="w-3 h-3 inline mr-0.5" />Direct</span>
            ) : isLinked
              ? <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Auto</span>
              : <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}><FileText className="w-3 h-3 inline mr-0.5" />Direct</span>
            }
          </td>
        );
      case 'comment':
        return (
          <td key={col.key} className={`py-3 px-3 text-xs max-w-[150px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            <span className="truncate block" title={activity.comments || ''}>{activity.comments || '—'}</span>
          </td>
        );
      case 'extraordinary':
        return (
          <td key={col.key} className="py-3 px-3 text-center" data-testid={`extraordinary-cell-${activity.id}`}>
            {activity.is_extraordinary
              ? <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-500/20 text-amber-600'}`}>{t('tableView.badgeExtraordinary')}</span>
              : <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-300'}`}>—</span>
            }
          </td>
        );
      case 'actions':
        return (
          <td key={col.key} className="py-3 px-3 text-right">
            {isLinked ? <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Auto</span> : (
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => handleEditActivityInModal(activity)} data-testid={`edit-activity-${activity.id}`}
                  disabled={isReadonly}
                  title={isReadonly ? t('dataEntry.actions.readonlyTooltip') : t('dataEntry.edit')}
                  className={`p-1.5 rounded-lg transition-colors ${isReadonly ? 'opacity-50 cursor-not-allowed' : (isDark ? 'hover:bg-slate-700 hover:text-blue-400' : 'hover:bg-blue-50 hover:text-blue-500')}`}>
                  <Edit3 className={`w-3.5 h-3.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                </button>
                <button onClick={() => activity.sale_id && activity.product_id ? handleEditActivityInModal(activity) : handleDeleteActivity(activity)}
                  data-testid={`delete-activity-${activity.id}`}
                  disabled={isReadonly}
                  title={isReadonly ? t('dataEntry.actions.readonlyTooltip') : (activity.sale_id ? t('dataEntry.manageSale') : t('dataEntry.delete'))}
                  className={`p-1.5 rounded-lg transition-colors ${isReadonly ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-500/10'}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500/70" />
                </button>
              </div>
            )}
          </td>
        );
      default:
        return <td key={col.key} />;
    }
  };

  // Count visible data cols (exclude actions) for footer colspan
  const dataColCount = visibleCols.filter(c => !['emissions', 'percent', 'actions'].includes(c.key)).length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" onClick={onClose}>
      <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }} onClick={e => e.stopPropagation()}
        className={`absolute inset-y-0 right-0 w-[94%] max-w-[1600px] ${isDark ? 'bg-slate-900' : 'bg-gray-50'} shadow-2xl flex flex-col`}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${headerBg} px-6 py-5`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Table className="w-5 h-5 text-white" /></div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {tableViewScope === null ? t('tableView.fullReport') : scopeLabels[tableViewScope]?.name}
                </h3>
                <p className="text-sm text-white/70">
                  {rawActivities.length} {t('tableView.entries')} — {formatEmissions(tableViewScope === null ? summary?.total_emissions : summary?.scope_emissions?.[tableViewScope]).value}{' '}
                  {formatEmissions(tableViewScope === null ? summary?.total_emissions : summary?.scope_emissions?.[tableViewScope]).unit}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <ColumnConfigPopover columns={columns} setColumns={setColumns} isDark={isDark} t={t} />
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white" data-testid="close-table-view"><X className="w-6 h-6" /></button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('tableView.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
                data-testid="table-view-search" />
            </div>
            {/* FEAT-01d — Filtre 3-états événement extraordinaire (Q3). */}
            {/* FEAT-01g #3 — affichage conditionnel : masqué si le périmètre ne
                contient aucune activité extraordinaire (Approche 1, gain de place). */}
            {hasExtraordinary && (
              <div data-testid="extraordinary-filter" className="flex items-center gap-0.5 p-0.5 rounded-lg bg-white/10 text-xs font-medium flex-shrink-0">
                {[
                  { key: 'all', label: t('tableView.filterExtraAll') },
                  { key: 'hide', label: t('tableView.filterExtraHide') },
                  { key: 'only', label: t('tableView.filterExtraOnly') },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setExtraordinaryFilter(opt.key)}
                    data-testid={`extraordinary-filter-${opt.key}`}
                    className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap ${
                      extraordinaryFilter === opt.key ? 'bg-white text-gray-900 shadow-sm' : 'text-white/70 hover:text-white'
                    }`}
                  >{opt.label}</button>
                ))}
              </div>
            )}
            {/* FEAT (2949441937) — filtre par catégorie (scope-aware, masqué si < 2). */}
            {presentCategoryIds.length >= 2 && (
              <select
                data-testid="category-filter"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/30 flex-shrink-0"
              >
                <option value="" className="text-gray-900">{t('tableView.filterCategoryAll')}</option>
                {presentCategoryIds.map(id => (
                  <option key={id} value={id} className="text-gray-900">{getCategoryName(id)}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {activities.length === 0 ? (
            <div className="text-center py-20">
              <Table className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-700' : 'text-gray-300'}`} />
              <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{searchQuery ? t('dataEntry.noResults') : t('dataEntry.noData')}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-800 border-b border-slate-700' : 'bg-white border-b border-gray-200'}`}>
                <tr>
                  {visibleCols.map(col => (
                    <th key={col.key}
                      className={`${thBase} ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.sortable ? 'cursor-pointer' : ''} ${isPriority(col.key) ? '' : 'max-lg:hidden'}`}
                      onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                        {t(col.labelKey)}
                        {col.sortable && <SortIcon field={col.key} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => {
                  const isLinked = activity.group_index > 0;
                  return (
                    <tr key={activity.id}
                      className={`border-b ${isDark ? 'border-slate-800' : 'border-gray-100'} transition-colors ${
                        isLinked ? (isDark ? 'bg-slate-800/60' : 'bg-gray-50/80') : (isDark ? 'hover:bg-slate-800/80' : 'hover:bg-white')
                      }`}>
                      {/* renderCell rend lui-même la cellule (un aiguillage de
                          onze branches). On étend sa classe ici plutôt que d'y
                          ouvrir chaque branche : un seul point d'accroche, et
                          la cellule reste rendue une seule fois — aucun nœud
                          dupliqué, donc aucun testid en double. */}
                      {visibleCols.map(col => {
                        const cell = renderCell(col, activity);
                        if (!cell || isPriority(col.key)) return cell;
                        return React.cloneElement(cell, {
                          className: `${cell.props.className || ''} max-lg:hidden`,
                        });
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={`sticky bottom-0 ${isDark ? 'bg-slate-800 border-t border-slate-700' : 'bg-white border-t border-gray-200'}`}>
                  <td colSpan={dataColCount} className={`py-3 px-3 font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {tableViewScope === null ? t('dataEntry.totalGeneral') : `${t('dataEntry.totalScope')} ${scopeLabels[tableViewScope]?.name}`}
                    {searchQuery && ` (${activities.length} ${t('tableView.results')})`}
                  </td>
                  {visibleCols.some(c => c.key === 'emissions') && (
                    <td className={`py-3 px-3 text-right font-bold text-sm ${
                      tableViewScope === null ? 'text-blue-500' : tableViewScope === 'scope1' ? 'text-blue-500' : tableViewScope === 'scope2' ? 'text-cyan-500' : 'text-purple-500'
                    }`}>{formatEmissionsForTable(activities.reduce((s, a) => s + (a.emissions || 0), 0), summary?.total_emissions)}</td>
                  )}
                  {/* RESP-3b — le pied doit compter autant de cellules que le
                      corps : celle-ci suit donc sa colonne, qui n'est pas
                      prioritaire. Le colSpan du libellé de total, lui, n'est
                      pas recalculé : le navigateur le borne au nombre de
                      colonnes restantes, si bien que le libellé s'étend
                      jusqu'au chiffre — ce qu'on veut. */}
                  {visibleCols.some(c => c.key === 'percent') && (
                    <td className={`py-3 px-3 text-right text-xs font-medium max-lg:hidden ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {totalEmissions > 0 ? (activities.reduce((s, a) => s + (a.emissions || 0), 0) / totalEmissions * 100).toFixed(1) : 0}%
                    </td>
                  )}
                  {visibleCols.filter(c => !['emissions', 'percent'].includes(c.key) && visibleCols.indexOf(c) >= visibleCols.findIndex(v => v.key === 'emissions')).length > 0 && (
                    <td colSpan={visibleCols.filter(c => {
                      const emIdx = visibleCols.findIndex(v => v.key === 'emissions');
                      const pctIdx = visibleCols.findIndex(v => v.key === 'percent');
                      const cIdx = visibleCols.indexOf(c);
                      return cIdx > Math.max(emIdx, pctIdx) && c.key !== 'emissions' && c.key !== 'percent';
                    }).length} />
                  )}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TableViewPanel;
